import type { ConfigLayer, IceServer, SignalingSpec } from "./types";

type Json = Record<string, unknown>;
const isObject = (v: unknown): v is Json =>
  typeof v === "object" && v !== null && !Array.isArray(v);
const isString = (v: unknown): v is string => typeof v === "string";

const ICE_URL = /^(stun|stuns|turn|turns):[^\s]+$/;

function isRelayUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (parsed.protocol === "wss:") return true;
    // Plain ws: only for local test relays.
    return (
      parsed.protocol === "ws:" && ["localhost", "127.0.0.1", "[::1]"].includes(parsed.hostname)
    );
  } catch {
    return false;
  }
}

/** iroh relays are reached over HTTPS (WebSocket). Plain http: only for local test relays. */
function isIrohRelayUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (parsed.protocol === "https:") return true;
    return (
      parsed.protocol === "http:" && ["localhost", "127.0.0.1", "[::1]"].includes(parsed.hostname)
    );
  } catch {
    return false;
  }
}

function iceServer(value: unknown, warn: (m: string) => void): IceServer | undefined {
  if (!isObject(value)) return void warn("ignoring ICE server that isn't an object");
  const urls = (Array.isArray(value.urls) ? value.urls : [value.urls]).filter(
    (u): u is string => isString(u) && ICE_URL.test(u),
  );
  if (urls.length === 0) return void warn("ignoring ICE server without valid stun:/turn: urls");
  const server: IceServer = { urls: urls.length === 1 ? (urls[0] as string) : urls };
  if (isString(value.username)) server.username = value.username;
  if (isString(value.credential)) server.credential = value.credential;
  return server;
}

function signalingSpec(
  value: unknown,
  warn: (m: string) => void,
  allowMemory: boolean,
): SignalingSpec | undefined {
  if (!isObject(value) || !isString(value.type))
    return void warn("ignoring malformed signaling entry");
  switch (value.type) {
    case "nostr": {
      const relays = (Array.isArray(value.relays) ? value.relays : []).filter(
        (r): r is string => isString(r) && isRelayUrl(r),
      );
      if (relays.length === 0) return void warn("ignoring nostr signaling without valid relays");
      return { type: "nostr", relays };
    }
    case "memory":
      // In-process signaling is for tests: never from a link, /config.json or settings.
      if (!allowMemory) return void warn('ignoring "memory" signaling (tests only)');
      return isString(value.channel)
        ? { type: "memory", channel: value.channel }
        : { type: "memory" };
    default:
      return void warn(`ignoring unsupported signaling type "${value.type}"`);
  }
}

/**
 * Keeps the valid parts of an untrusted config layer (JSON from /config.json, storage, env, or a
 * link). Invalid entries are dropped with a warning rather than failing the whole layer.
 */
export function validateLayer(
  value: unknown,
  warn: (message: string) => void = () => {},
  options: { allowMemory?: boolean } = {},
): ConfigLayer {
  if (!isObject(value)) {
    if (value !== undefined) warn("ignoring config that isn't an object");
    return {};
  }
  const layer: ConfigLayer = {};
  if (value.iceServers !== undefined) {
    if (Array.isArray(value.iceServers)) {
      layer.iceServers = value.iceServers
        .map((s) => iceServer(s, warn))
        .filter((s): s is IceServer => s !== undefined);
    } else warn("ignoring iceServers: not an array");
  }
  if (value.signaling !== undefined) {
    if (Array.isArray(value.signaling)) {
      const specs = value.signaling
        .map((s) => signalingSpec(s, warn, options.allowMemory ?? false))
        .filter((s): s is SignalingSpec => s !== undefined);
      if (specs.length > 0) layer.signaling = specs;
      else warn("ignoring signaling: no usable entries");
    } else warn("ignoring signaling: not an array");
  }
  if (value.irohRelays !== undefined) {
    if (Array.isArray(value.irohRelays)) {
      const dropped = value.irohRelays.filter((u) => !(isString(u) && isIrohRelayUrl(u)));
      for (const url of dropped)
        warn(`ignoring iroh relay ${JSON.stringify(url)}: not an https: URL`);
      // An empty list is meaningful: it turns the fallback off.
      layer.irohRelays = value.irohRelays.filter((u): u is string => !dropped.includes(u));
    } else warn("ignoring irohRelays: not an array");
  }
  if (value.iceTransportPolicy !== undefined) {
    if (value.iceTransportPolicy === "all" || value.iceTransportPolicy === "relay") {
      layer.iceTransportPolicy = value.iceTransportPolicy;
    } else warn('ignoring iceTransportPolicy: expected "all" or "relay"');
  }
  return layer;
}
